import { BrandSelect } from "components/BrandSelect/BrandSelect"
import { ColorItem } from "components/ColorItem/ColorItem"
import { FixedHeaderLayout } from "components/FixedHeaderLayout/FixedHeaderLayout"
import { ImageUpload } from "components/ImageUpload"
import { ProductCategories, ProductCategoriesLink } from "components/Navbar/types"
import PageLoader from "components/PageLoader"
import { Preview } from "components/Preview"
import { ProductCardPreview } from "components/ProductCardPreview"
import { useAppSelector } from "hooks/storeHooks"
import { useCurrentId } from "hooks/useCurrentId"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import EffectSettings from "services/effect_settings"
import { tintVTOService } from "services/tintvto"
import { IntegrationType, ProductV2 } from "types"
import {
  Box,
  Button,
  ContentLayout,
  Option,
  SingleSelect,
  SingleSelectOption,
  Typography,
} from "@strapi/design-system"
import { Plus } from "@strapi/icons"
import { SelectWithInfoTooltip } from "./SelectWithInfoTooltip"
import { TextInputWithInfoTooltip } from "./TextInputWithInfoTooltip"
import { CATEGORIES } from "./mock"
import { Beauty } from "services/player"
import { useSaveProductMutation } from "hooks/query/useProductsQuery"
import { z } from "zod"
import { Errors } from "pages/Products/ProductPage/Errors"
import { selectTypes } from "store/product/productSlice"
import { ProductTranslationsModal } from "components/ProductTranslations/ProductTranslationsModal"
import { ProductTranslationsData } from "components/ProductTranslations/ProductTranslations"

const TINT_VIEWER_SIZE = 230

const initialProductItem: ProductWithColors = {
  brandSlug: "",
  categoryId: 0,
  coverage: "",
  finish: "",
  id: "",
  merchant: "",
  region: "",
  sku: "",
  type: "",
  upc: "",
  url: "",
  brand: "",
  name: "",
  colors: [],
  cover: "",
}

const PRODUCT_TYPE_REGIONS: Record<string, string[]> = {
  EYELINER: ["eyeliner"],
  EYESHADOW: ["eyeshadow"],
  MASCARA: ["eyelashes"],
  BLUSH: ["blush"],
  FOUNDATION: ["foundation"],
  LIPSTICK: ["lipstick"],
  EYEBROWS: ["eyebrows"],
  HIGHLIGHTER: ["highlighter"],
  CONTOUR: ["contour"],
  CONCEALER: ["concealer"],
  EYE_COLOR: ["eyeshadow"],
  LIPLINER: ["lipsliner"],
  GLOSS: ["lipstick"],
  BB_CC_CREAM: ["foundation"],
  POWDER: ["foundation"],
  EYES_AND_BROWS: ["eyebrows", "eyeshadow"],
  CHEEKS: ["blush", "contour"],
  BRONZER: ["blush", "contour"],
  PRIMERS: ["foundation"],
  LIP_BALM: ["lipstic"],
  FOUNDATION_CLEAR: ["concealer", "foundation"],
  FOUNDATION_MEDIUM_CLEAR: ["concealer", "foundation"],
  FOUNDATION_MEDIUM: ["concealer", "foundation"],
  FOUNDATION_MEDIUM_DARK: ["concealer", "foundation"],
  FOUNDATION_DARK: ["concealer", "foundation"],
}

interface ProductPageProps {
  add?: boolean
}

let id = 0
const createId = () => (id++).toString()

type ProductWithColors = Omit<ProductV2, "colors"> & {
  colors: Array<ProductV2["colors"][0] & { id?: string }>
}

const createProductSchema = (t: (key: string) => string) => {
  // First, create a schema for the color object
  const colorSchema = z.object({
    color: z.string().min(1, t("errors.colorRequired")),
    sku: z.string().min(1, t("errors.skuRequired")),
    name: z.string().min(1, t("errors.colorNameRequired")),
  })

  // Then create the main product schema
  return z.object({
    name: z.string().min(1, t("errors.nameRequired")),
    brand: z.string().min(1, t("errors.brandRequired")),
    typeId: z.string().min(1, t("errors.typeRequired")),
    cover: z.string().min(1, t("errors.coverImageRequired")),
    region: z.string().min(1, t("errors.regionRequired")),
    finish: z.string().min(1, t("errors.finishRequired")),
    coverage: z.string().min(1, t("errors.coverageRequired")),
    colors: z.array(colorSchema).min(1, t("errors.atLeastOneColorRequired")),
    merchant: z.string().min(1, t("errors.atLeastOneMerchantRequired")),
  })
}

export const ProductPage = ({ add }: ProductPageProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userInfo = useAppSelector((state) => state.user.userInfo)
  const selectedProduct = useAppSelector((state) => state.products.product)
  const types = useAppSelector(selectTypes)
  const productId = useCurrentId()

  const [productItem, setProductItem] = useState<ProductWithColors>(initialProductItem)
  const [merchantIds, setMerchantIds] = useState([])
  const [activeColorId, setActiveColorId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [translations, setTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [isTranslationsModalOpen, setIsTranslationsModalOpen] = useState(false)
  const [pendingTranslations, setPendingTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [translationsModified, setTranslationsModified] = useState(false)
  const [savedLanguages, setSavedLanguages] = useState<string[]>([])

  const refetchTranslations = async () => {
    if (!productId || !productItem.colors?.length) return
    const skuIds = productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
    if (skuIds.length > 0)
      try {
        const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
        const loadedProductName = translationsData.alternateName || []
        const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce((acc, item) => {
          acc[item.skuId] = item.skuI8N || []
          return acc
        }, {} as Record<string, Array<{ code: string; name: string }>>)

        setTranslations({
          productName: loadedProductName,
          skuNames: loadedSkuNames,
        })
        setSavedLanguages(loadedProductName.map((t) => t.code))
      } catch (error) {
        // Failed to load translations
      }
  }
  const sortedTypes = [...types].sort((a, b) => a.name.localeCompare(b.name))
  const getRegionsFrom = (item) =>
    types.find((t) => t.id === item.typeId)?.name
      ? PRODUCT_TYPE_REGIONS[types.find((t) => t.id === item.typeId).name] || []
      : []
  const productSchema = createProductSchema(t)
  const [errors, setErrors] = useState<z.ZodFormattedError<z.infer<typeof productSchema>> | null>(
    null
  )

  const getFinishesFrom = (item) => Object.keys(EffectSettings[item.region] || {})
  const getCoveragesFrom = (item) =>
    Object.keys(EffectSettings[item.region]?.[item.finish]?.K || {})

  const updateProductItem = (newProps: Partial<ProductWithColors>) => {
    setProductItem((prev) => {
      const colorsWithIds =
        { ...prev, ...newProps }.colors?.map((c) =>
          c.id
            ? c
            : {
                id: createId(),
                ...c,
              }
        ) || []

      setActiveColorId(colorsWithIds[0]?.id || null)

      const updated = { ...prev, ...newProps, colors: colorsWithIds }

      if (activeColorId === null && updated.colors?.length > 0)
        setActiveColorId(updated.colors[0]?.id)
      if (updated.typeId && !updated.region) updated.region = getRegionsFrom(updated)[0] || ""
      if (updated.region && !updated.finish) updated.finish = getFinishesFrom(updated)[0] || ""
      if (updated.finish && !updated.coverage) updated.coverage = getCoveragesFrom(updated)[0] || ""

      return updated
    })
  }
  useEffect(() => {
    validateForm()
  }, [productItem])

  // Sync pendingTranslations with new variants when adding new product
  useEffect(() => {
    if (!add) return

    const currentSkuIds = productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
    const existingSkuIds = Object.keys(pendingTranslations.skuNames)

    // Find new variants that don't have translations yet
    const newSkuIds = currentSkuIds.filter((skuId) => !existingSkuIds.includes(skuId))

    if (newSkuIds.length > 0 && pendingTranslations.productName.length > 0)
      setPendingTranslations((prev) => {
        const updatedSkuNames = { ...prev.skuNames }

        newSkuIds.forEach((skuId) => {
          // Create empty translations for all existing languages for the new variant
          updatedSkuNames[skuId] = prev.productName.map((t) => ({
            code: t.code,
            name: "",
          }))
        })

        return {
          ...prev,
          skuNames: updatedSkuNames,
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productItem.colors?.map((c) => c.skuId || c.sku).join(","), add])
  useEffect(() => {
    ;(async () => {
      setIsLoading(true)

      if (userInfo.groups.includes("Admin")) {
        const { data } = await tintVTOService.merchants()
        setMerchantIds(data?.merchants?.map((m) => ({ id: m.id, name: m.name })) || [])
      }

      if (productId && !add) {
        const product = await tintVTOService.productByIdV2(productId)
        updateProductItem({
          ...product,
          typeId: types.find(({ name }) => name === product.type)?.id,
          categoryId: CATEGORIES[0].id,
          cover: product.cover,
          merchant: product.merchant?.length ? product.merchant : userInfo.merchantId,
        })

        const skuIds = product.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
        if (skuIds.length > 0)
          try {
            const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
            const loadedProductName = translationsData.alternateName || []
            const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce(
              (acc, item) => {
                acc[item.skuId] = item.skuI8N || []
                return acc
              },
              {} as Record<string, Array<{ code: string; name: string }>>
            )

            setTranslations({
              productName: loadedProductName,
              skuNames: loadedSkuNames,
            })
            setSavedLanguages(loadedProductName.map((t) => t.code))
          } catch (error) {
            // Failed to load translations
          }
      } else if (userInfo.integrationType !== IntegrationType.COMMON) {
        selectedProduct
          ? updateProductItem({
              ...selectedProduct,
              categoryId: CATEGORIES[0].id,
            } as ProductWithColors)
          : navigate(ProductCategoriesLink.MAKEUP)
      } else {
        updateProductItem({
          ...initialProductItem,
          categoryId: CATEGORIES[0].id,
          merchant: userInfo.merchantId,
          colors: [],
        })
      }

      setIsLoading(false)
    })()
    return () => void Beauty.reset()
  }, [productId])

  const saveTranslationsAfterProductSave = async (
    productId: string,
    skuIds: string[],
    translations: ProductTranslationsData
  ) => {
    try {
      const savedProduct = await tintVTOService.productByIdV2(productId)

      // Create mapping from sku to skuId for all variants
      const skuToSkuIdMap: Record<string, string> = {}
      savedProduct.colors?.forEach((color) => {
        if (color.skuId && color.sku) skuToSkuIdMap[color.sku] = color.skuId
        if (color.skuId) skuToSkuIdMap[color.skuId] = color.skuId
      })

      // Update translations.skuNames keys from sku to skuId
      // For new products, keys might be sku strings, need to map to actual skuId
      const updatedSkuNames: Record<string, any[]> = {}
      Object.entries(translations.skuNames || {}).forEach(([key, skuTranslations]) => {
        // Try to find skuId by matching sku
        let actualSkuId = skuToSkuIdMap[key]

        // If not found, try to find by matching sku in saved product
        if (!actualSkuId) {
          const matchingColor = savedProduct.colors?.find(
            (c) => (c.skuId || c.sku) === key || c.sku === key
          )
          if (matchingColor?.skuId) actualSkuId = matchingColor.skuId
        }

        // If still not found, use key as-is (might already be skuId)
        if (!actualSkuId) actualSkuId = key

        if (actualSkuId && skuTranslations) updatedSkuNames[actualSkuId] = skuTranslations
      })

      // Save product translations
      const productTranslationsToSave = (translations.productName || []).filter(
        (t) => t.name && t.name.trim() && t.code !== "en"
      )
      if (productTranslationsToSave.length > 0)
        await tintVTOService.saveProductTranslation(
          productId,
          productItem.merchant || userInfo.merchantId,
          productTranslationsToSave
        )

      // Save SKU translations for all variants that have translations
      const skuTranslationsToSave: Array<{ skuId: string; translations: any[] }> = []
      Object.entries(updatedSkuNames).forEach(([skuId, skuTranslations]) => {
        const translationsWithNames = (skuTranslations || []).filter(
          (t) => t.name && t.name.trim() && t.code !== "en"
        )
        if (translationsWithNames.length > 0)
          skuTranslationsToSave.push({ skuId, translations: translationsWithNames })
      })

      if (skuTranslationsToSave.length > 0)
        await Promise.all(
          skuTranslationsToSave.map(({ skuId, translations: skuTrans }) =>
            tintVTOService.saveSKUTranslation(
              skuId,
              productItem.merchant || userInfo.merchantId,
              skuTrans
            )
          )
        )

      // Update saved languages after saving
      setSavedLanguages(productTranslationsToSave.map((t) => t.code))

      // Refetch translations if productId exists (for existing products)
      if (productId) await refetchTranslations()
    } catch (error) {
      console.error("Failed to save translations:", error)
    }
  }

  const { isPending, mutate: saveProductMutation } = useSaveProductMutation(
    userInfo.merchantId || "",
    ProductCategories.MAKEUP,
    saveTranslationsAfterProductSave
  )

  const validateForm = () => {
    // Don't show validation errors while data is still loading
    if (isLoading) return true

    try {
      // Parse and validate the form data
      productSchema.parse(productItem)
      setErrors(null)
      return true
    } catch (error) {
      if (error instanceof z.ZodError) setErrors(error.format())
      return false
    }
  }

  const handleRemoveColor = (colorId: string) => {
    updateProductItem({
      colors: productItem.colors.filter((color) => color.id !== colorId),
    })
    if (activeColorId === colorId) setActiveColorId(productItem.colors[0]?.id || null)
  }

  const handleAddColor = () => {
    const newColor = {
      id: createId(),
      color: "#ececec",
      upc: "",
      sku: "",
      name: "",
    }
    updateProductItem({
      colors: [...productItem.colors, newColor],
    })
    if (!activeColorId) setActiveColorId(newColor.id)

    // If adding new product, create empty translations for the new variant
    if (add && pendingTranslations.productName.length > 0) {
      // The useEffect will handle this, but we can also do it here for immediate update
      // The sku will be empty initially, so translations will be created when sku is set
    }
  }

  const handleColorObjectChanged = (key: string, val: any, colorId: string) => {
    setProductItem((prev: any) => ({
      ...prev,
      colors: prev.colors.map((c) => (c.id === colorId ? { ...c, [key]: val } : c)),
    }))
    setActiveColorId(colorId)
  }

  const handleFileChange = (file: File) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      updateProductItem({
        file,
        cover: imageUrl,
      })
    }
  }

  // Show loading state while data is being fetched
  if (isLoading)
    return (
      <Box>
        <FixedHeaderLayout
          title={add ? t("productPage.addProductTitle") : t("productPage.modifyProductTitle")}
          onBack={() => navigate(ProductCategoriesLink.MAKEUP)}
          onFirstButtonClicked={() => navigate(ProductCategoriesLink.MAKEUP)}
          onSecondButtonClicked={() => saveProductMutation(productItem as ProductV2)}
          onSecondButtonLoading={isPending}
          secondButtonDisabled={true}
          validateSecondButton={validateForm}
        />
        <ContentLayout className="overflow-hidden">
          <PageLoader />
        </ContentLayout>
      </Box>
    )

  return (
    <Box>
      <FixedHeaderLayout
        title={add ? t("productPage.addProductTitle") : t("productPage.modifyProductTitle")}
        onBack={() => navigate(ProductCategoriesLink.MAKEUP)}
        onFirstButtonClicked={() => navigate(ProductCategoriesLink.MAKEUP)}
        onSecondButtonClicked={() => {
          const translationsToSave = add ? pendingTranslations : translations

          const hasProductTranslations =
            translationsToSave?.productName?.some(
              (t) => t.name && t.name.trim() && t.code !== "en"
            ) || false

          const hasSkuTranslations =
            translationsToSave?.skuNames &&
            Object.keys(translationsToSave.skuNames).length > 0 &&
            Object.values(translationsToSave.skuNames).some((skuTrans) =>
              skuTrans?.some((t) => t.name && t.name.trim() && t.code !== "en")
            )

          const hasTranslationData =
            translationsModified || hasProductTranslations || hasSkuTranslations

          const productToSave = {
            ...(productItem as ProductV2),
            ...(hasTranslationData ? { pendingTranslations: translationsToSave } : {}),
          }
          saveProductMutation(productToSave)
        }}
        onSecondButtonLoading={isPending}
        secondButtonDisabled={errors !== null && Object.keys(errors).length > 1}
        validateSecondButton={validateForm}
      />
      <ContentLayout className="overflow-hidden">
        {productItem ? (
          <div className="flex flex-row gap-8 relative">
            <div className="flex flex-col gap-4 flex-1">
              <Box
                background="neutral0"
                className="!p-8 rounded-lg shadow-tableShadow flex flex-col gap-2"
              >
                <Box className="flex items-center justify-between">
                  <Typography variant="beta">{t("productPage.productCardInfo")}</Typography>
                  {(productId || add) && (
                    <Button
                      variant="secondary"
                      size="S"
                      onClick={() => setIsTranslationsModalOpen(true)}
                    >
                      {t("productPage.modifyTranslations")}
                    </Button>
                  )}
                </Box>
                <TextInputWithInfoTooltip
                  label={t("productPage.productNameLabel")}
                  name="product_name"
                  onChange={(e) => updateProductItem({ name: e.target.value })}
                  value={productItem.name}
                  description={t("productPage.productNameDescription")}
                  errors={errors?.name?._errors}
                />
                {userInfo.groups.includes("Admin") && (
                  <>
                    <SingleSelect
                      label={t("productPage.merchantIdLabel")}
                      onChange={(value) => updateProductItem({ merchant: value })}
                      value={productItem.merchant}
                    >
                      {merchantIds.map((m) => (
                        <SingleSelectOption key={m.id} value={m.id}>
                          {m.name} - {m.id}
                        </SingleSelectOption>
                      ))}
                    </SingleSelect>
                    <Errors {...errors?.merchant} />
                  </>
                )}
                <BrandSelect
                  value={productItem.brand}
                  onChange={(value) => updateProductItem({ brand: value })}
                  description={t("productPage.selectAnExistingBrand")}
                  errors={errors?.brand?._errors}
                  label={t("productPage.brandLabel")}
                />
                <TextInputWithInfoTooltip
                  label={t("productPage.storeProductUrl")}
                  description={t("productPage.productPageUrlLabel")}
                  name="url"
                  onChange={(e) => updateProductItem({ url: e.target.value })}
                  value={productItem.url || ""}
                  isOptional={true}
                />
                <ImageUpload onFileChange={handleFileChange} errors={errors?.cover?._errors} />
              </Box>

              <Box
                background="neutral0"
                className="!p-8 rounded-lg shadow-tableShadow row-start-2 col-start-1 row-end-3 col-end-4
          flex flex-col items-start justify-start"
              >
                <Box className="flex flex-col gap-4 mb-10">
                  <Typography variant="beta">{t("productPage.vtoInfoTitle")}</Typography>
                  <Box className="flex flex-row flex-wrap gap-4">
                    <SelectWithInfoTooltip
                      name="product_type"
                      label={t("productPage.productTypeLabel")}
                      onChange={(value) =>
                        updateProductItem({ typeId: value, region: "", finish: "", coverage: "" })
                      }
                      value={productItem.typeId}
                      errors={errors?.typeId?._errors}
                      description={t("productPage.productTypeDescription")}
                    >
                      {sortedTypes.map((to) => (
                        <Option key={to.id} value={to.id}>
                          {to.name}
                        </Option>
                      ))}
                    </SelectWithInfoTooltip>
                    <SelectWithInfoTooltip
                      label={t("productPage.regionLabel")}
                      name="region"
                      onChange={(value) =>
                        updateProductItem({ region: value, finish: "", coverage: "" })
                      }
                      disabled={!productItem.typeId?.length}
                      errors={errors?.region?._errors}
                      value={productItem.region}
                      description={t("productPage.regionDescription")}
                    >
                      {getRegionsFrom(productItem).map((region) => (
                        <Option key={region} value={region}>
                          {region}
                        </Option>
                      ))}
                    </SelectWithInfoTooltip>
                    <SelectWithInfoTooltip
                      label={t("productPage.finishLabel")}
                      name="finish"
                      onChange={(value) => updateProductItem({ finish: value })}
                      disabled={!productItem.region?.length}
                      value={productItem.finish}
                      errors={errors?.finish?._errors}
                      description={t("productPage.finishDescription")}
                    >
                      {getFinishesFrom(productItem).map((finish) => (
                        <Option key={finish} value={finish}>
                          {finish}
                        </Option>
                      ))}
                    </SelectWithInfoTooltip>
                    <SelectWithInfoTooltip
                      disabled={!productItem.finish?.length}
                      errors={errors?.coverage?._errors}
                      label={t("productPage.coverageLabel")}
                      name="coverage"
                      onChange={(value) => updateProductItem({ coverage: value })}
                      value={productItem.coverage}
                      description={t("productPage.coverageDescription")}
                    >
                      {getCoveragesFrom(productItem).map((coverage) => (
                        <Option key={coverage} value={coverage}>
                          {coverage}
                        </Option>
                      ))}
                    </SelectWithInfoTooltip>
                  </Box>
                </Box>

                <Box className="!pt-8 flex flex-col items-start gap-4">
                  <Box className="flex flex-row gap-4 items-center justify-start">
                    <Typography className="font-semibold">
                      {t("productPage.productColors")}
                    </Typography>
                    {userInfo.integrationType === IntegrationType.COMMON && (
                      <Button startIcon={<Plus />} onClick={handleAddColor}>
                        {t("productPage.addColor")}
                      </Button>
                    )}
                    <Errors {...errors?.colors} />
                  </Box>
                  <Box className="flex flex-col gap-4 items-start">
                    {productItem.colors?.map((colorObj, index) => (
                      <Box key={colorObj.id} className="w-full">
                        <Box className="flex justify-between items-start gap-2">
                          <Box className="w-[34%]">
                            <TextInputWithInfoTooltip
                              label={t("productPage.variantNameLabel")}
                              onChange={(e) =>
                                handleColorObjectChanged("name", e.target.value, colorObj.id)
                              }
                              value={colorObj.name}
                              name="variant_name"
                              description={t("productPage.variantNameDescription")}
                            />
                          </Box>
                          <Box className="w-[46%]">
                            <TextInputWithInfoTooltip
                              disabled={userInfo.integrationType !== IntegrationType.COMMON}
                              label={t("productPage.skuLabel")}
                              name="sku"
                              onChange={(e) =>
                                handleColorObjectChanged("sku", e.target.value, colorObj.id)
                              }
                              value={colorObj.sku}
                              description={t("productPage.skuDescription")}
                            />
                          </Box>
                          <Box className="max-w-[180px] flex flex-col gap-2 items-start justify-center">
                            <Typography
                              variant="pi"
                              className="text-center font-semibold text-xs h-3"
                            >
                              {t("productPage.productColor")}
                            </Typography>
                            <ColorItem
                              value={colorObj.color}
                              onChange={(value) =>
                                handleColorObjectChanged("color", value, colorObj.id)
                              }
                              onDelete={
                                userInfo.integrationType === IntegrationType.COMMON
                                  ? () => handleRemoveColor(colorObj.id)
                                  : undefined
                              }
                            />
                          </Box>
                        </Box>
                        <Errors
                          _errors={[
                            ...((errors?.colors || [])[index]?.name?._errors || []),
                            ...((errors?.colors || [])[index]?.sku?._errors || []),
                            ...((errors?.colors || [])[index]?.color?._errors || []),
                          ]}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </div>
            <div className="flex flex-col gap-4" style={{ minWidth: "300px", maxWidth: "400px" }}>
              <div
                className="h-fit rounded-lg shadow-tableShadow bg-neutral100
                  flex flex-col gap-4 justify-start items-center !p-4
                  self-start sticky top-0 !border-1 !border-solid"
                style={{
                  borderColor: "rgb(74, 74, 106)",
                  maxHeight: productItem.colors?.length >= 1 ? "730px" : "685px",
                }}
              >
                <ProductCardPreview
                  imageSrc={productItem.cover}
                  sku={
                    productItem.colors?.length
                      ? productItem.colors.find((c) => c.id === activeColorId)?.name || ""
                      : ""
                  }
                  brand={productItem.brand}
                  productName={productItem.name || ""}
                  color={
                    productItem.colors?.length
                      ? productItem.colors.find((c) => c.id === activeColorId)?.color
                      : null
                  }
                />
                <Preview
                  disabled={
                    !productItem.coverage ||
                    !productItem.region ||
                    !productItem.finish ||
                    productItem.colors.length < 1
                  }
                  type={ProductCategories.MAKEUP}
                  size={TINT_VIEWER_SIZE}
                  productItem={productItem}
                  setActiveColorIndex={(index) => {
                    setActiveColorId(productItem.colors[index]?.id || null)
                  }}
                  activeColorIndex={productItem.colors.findIndex((c) => c.id === activeColorId)}
                />
              </div>
            </div>
          </div>
        ) : (
          <PageLoader />
        )}
      </ContentLayout>
      {(productId || add) && (
        <ProductTranslationsModal
          isOpen={isTranslationsModalOpen}
          onClose={() => setIsTranslationsModalOpen(false)}
          productId={productId || ""}
          merchantId={productItem.merchant || userInfo.merchantId}
          skuIds={productItem.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []}
          defaultProductName={productItem.name}
          defaultSkuNames={
            productItem.colors?.reduce((acc, c) => {
              const skuId = c.skuId || c.sku
              if (skuId) acc[skuId] = c.name
              return acc
            }, {} as Record<string, string>) || {}
          }
          defaultSkuNumbers={
            productItem.colors?.reduce((acc, c) => {
              const skuId = c.skuId || c.sku
              if (skuId) acc[skuId] = c.sku
              return acc
            }, {} as Record<string, string>) || {}
          }
          translations={add ? pendingTranslations : translations}
          onChange={(newTranslations) => {
            setTranslationsModified(true)
            if (add) setPendingTranslations(newTranslations)
            else setTranslations(newTranslations)
          }}
          onSaveSuccess={add ? undefined : refetchTranslations}
          savedLanguages={savedLanguages}
        />
      )}
    </Box>
  )
}
